/**
 * English fallback names for every code in `LanguageCode`. Used when the browser's
 * `Intl.DisplayNames` returns the raw code (some trimmed ICU builds do not ship data for
 * less common languages, and the enum also contains a few legacy Windows LCID codes that
 * are not valid BCP47).
 */
export const LANGUAGE_NAME_FALLBACKS: Record<string, string> = {
    aa: 'Afar',
    ab: 'Abkhazian',
    ae: 'Avestan',
    af: 'Afrikaans',
    ak: 'Akan',
    am: 'Amharic',
    an: 'Aragonese',
    ar: 'Arabic',
    'ar-ae': 'Arabic (United Arab Emirates)',
    'ar-bh': 'Arabic (Bahrain)',
    'ar-dz': 'Arabic (Algeria)',
    'ar-eg': 'Arabic (Egypt)',
    'ar-iq': 'Arabic (Iraq)',
    'ar-jo': 'Arabic (Jordan)',
    'ar-kw': 'Arabic (Kuwait)',
    'ar-lb': 'Arabic (Lebanon)',
    'ar-ly': 'Arabic (Libya)',
    'ar-ma': 'Arabic (Morocco)',
    'ar-om': 'Arabic (Oman)',
    'ar-qa': 'Arabic (Qatar)',
    'ar-sa': 'Arabic (Saudi Arabia)',
    'ar-sy': 'Arabic (Syria)',
    'ar-tn': 'Arabic (Tunisia)',
    'ar-ye': 'Arabic (Yemen)',
    as: 'Assamese',
    av: 'Avaric',
    ay: 'Aymara',
    az: 'Azerbaijani',
    ba: 'Bashkir',
    be: 'Belarusian',
    bg: 'Bulgarian',
    bh: 'Bhojpuri',
    bi: 'Bislama',
    bm: 'Bambara',
    bn: 'Bangla',
    bo: 'Tibetan',
    br: 'Breton',
    bs: 'Bosnian',
    ca: 'Catalan',
    ce: 'Chechen',
    ch: 'Chamorro',
    co: 'Corsican',
    cr: 'Cree',
    cs: 'Czech',
    cu: 'Church Slavic',
    cv: 'Chuvash',
    cy: 'Welsh',
    da: 'Danish',
    de: 'German',
    'de-at': 'Austrian German',
    'de-ch': 'Swiss High German',
    'de-de': 'German (Germany)',
    'de-li': 'German (Liechtenstein)',
    'de-lu': 'German (Luxembourg)',
    div: 'Divehi',
    dv: 'Divehi',
    dz: 'Dzongkha',
    ee: 'Ewe',
    el: 'Greek',
    en: 'English',
    'en-au': 'Australian English',
    'en-bz': 'English (Belize)',
    'en-ca': 'Canadian English',
    'en-cb': 'English (Caribbean)',
    'en-gb': 'British English',
    'en-ie': 'English (Ireland)',
    'en-jm': 'English (Jamaica)',
    'en-nz': 'English (New Zealand)',
    'en-ph': 'English (Philippines)',
    'en-tt': 'English (Trinidad & Tobago)',
    'en-us': 'American English',
    'en-za': 'English (South Africa)',
    'en-zw': 'English (Zimbabwe)',
    eo: 'Esperanto',
    es: 'Spanish',
    'es-ar': 'Spanish (Argentina)',
    'es-bo': 'Spanish (Bolivia)',
    'es-cl': 'Spanish (Chile)',
    'es-co': 'Spanish (Colombia)',
    'es-cr': 'Spanish (Costa Rica)',
    'es-do': 'Spanish (Dominican Republic)',
    'es-ec': 'Spanish (Ecuador)',
    'es-es': 'European Spanish',
    'es-gt': 'Spanish (Guatemala)',
    'es-hn': 'Spanish (Honduras)',
    'es-mx': 'Mexican Spanish',
    'es-ni': 'Spanish (Nicaragua)',
    'es-pa': 'Spanish (Panama)',
    'es-pe': 'Spanish (Peru)',
    'es-pr': 'Spanish (Puerto Rico)',
    'es-py': 'Spanish (Paraguay)',
    'es-sv': 'Spanish (El Salvador)',
    'es-us': 'Spanish (United States)',
    'es-uy': 'Spanish (Uruguay)',
    'es-ve': 'Spanish (Venezuela)',
    et: 'Estonian',
    eu: 'Basque',
    fa: 'Persian',
    ff: 'Fula',
    fi: 'Finnish',
    fj: 'Fijian',
    fo: 'Faroese',
    fr: 'French',
    'fr-be': 'French (Belgium)',
    'fr-ca': 'Canadian French',
    'fr-ch': 'Swiss French',
    'fr-fr': 'French (France)',
    'fr-lu': 'French (Luxembourg)',
    'fr-mc': 'French (Monaco)',
    fy: 'Western Frisian',
    ga: 'Irish',
    gd: 'Scottish Gaelic',
    gl: 'Galician',
    gn: 'Guarani',
    gu: 'Gujarati',
    gv: 'Manx',
    ha: 'Hausa',
    he: 'Hebrew',
    hi: 'Hindi',
    ho: 'Hiri Motu',
    hr: 'Croatian',
    'hr-ba': 'Croatian (Bosnia & Herzegovina)',
    'hr-hr': 'Croatian (Croatia)',
    ht: 'Haitian Creole',
    hu: 'Hungarian',
    hy: 'Armenian',
    hz: 'Herero',
    ia: 'Interlingua',
    id: 'Indonesian',
    ie: 'Interlingue',
    ig: 'Igbo',
    ii: 'Sichuan Yi',
    ik: 'Inupiaq',
    in: 'Indonesian',
    io: 'Ido',
    is: 'Icelandic',
    it: 'Italian',
    'it-ch': 'Italian (Switzerland)',
    'it-it': 'Italian (Italy)',
    iu: 'Inuktitut',
    iw: 'Hebrew',
    ja: 'Japanese',
    ji: 'Yiddish',
    jv: 'Javanese',
    jw: 'Javanese',
    ka: 'Georgian',
    kg: 'Kongo',
    ki: 'Kikuyu',
    kj: 'Kuanyama',
    kk: 'Kazakh',
    kl: 'Kalaallisut',
    km: 'Khmer',
    kn: 'Kannada',
    ko: 'Korean',
    kok: 'Konkani',
    kr: 'Kanuri',
    ks: 'Kashmiri',
    ku: 'Kurdish',
    kv: 'Komi',
    kw: 'Cornish',
    ky: 'Kyrgyz',
    kz: 'Kazakh',
    la: 'Latin',
    lb: 'Luxembourgish',
    lg: 'Ganda',
    li: 'Limburgish',
    ln: 'Lingala',
    lo: 'Lao',
    ls: 'Sesotho',
    lt: 'Lithuanian',
    lu: 'Luba-Katanga',
    lv: 'Latvian',
    mg: 'Malagasy',
    mh: 'Marshallese',
    mi: 'Māori',
    mk: 'Macedonian',
    ml: 'Malayalam',
    mn: 'Mongolian',
    mo: 'Romanian',
    mr: 'Marathi',
    ms: 'Malay',
    'ms-bn': 'Malay (Brunei)',
    'ms-my': 'Malay (Malaysia)',
    mt: 'Maltese',
    my: 'Burmese',
    na: 'Nauru',
    nb: 'Norwegian Bokmål',
    nd: 'North Ndebele',
    ne: 'Nepali',
    ng: 'Ndonga',
    nl: 'Dutch',
    'nl-be': 'Flemish',
    'nl-nl': 'Dutch (Netherlands)',
    nn: 'Norwegian Nynorsk',
    no: 'Norwegian',
    nr: 'South Ndebele',
    ns: 'Northern Sotho',
    nv: 'Navajo',
    ny: 'Nyanja',
    oc: 'Occitan',
    oj: 'Ojibwa',
    om: 'Oromo',
    or: 'Odia',
    os: 'Ossetic',
    pa: 'Punjabi',
    pi: 'Pali',
    pl: 'Polish',
    ps: 'Pashto',
    pt: 'Portuguese',
    'pt-br': 'Brazilian Portuguese',
    'pt-pt': 'European Portuguese',
    qu: 'Quechua',
    'qu-bo': 'Quechua (Bolivia)',
    'qu-ec': 'Quechua (Ecuador)',
    'qu-pe': 'Quechua (Peru)',
    rm: 'Romansh',
    rn: 'Rundi',
    ro: 'Romanian',
    ru: 'Russian',
    rw: 'Kinyarwanda',
    sa: 'Sanskrit',
    sb: 'Sorbian',
    sc: 'Sardinian',
    sd: 'Sindhi',
    se: 'Northern Sami',
    'se-fi': 'Northern Sami (Finland)',
    'se-no': 'Northern Sami (Norway)',
    'se-se': 'Northern Sami (Sweden)',
    sg: 'Sango',
    sh: 'Serbian (Latin)',
    si: 'Sinhala',
    sk: 'Slovak',
    sl: 'Slovenian',
    sm: 'Samoan',
    sn: 'Shona',
    so: 'Somali',
    sq: 'Albanian',
    sr: 'Serbian',
    'sr-ba': 'Serbian (Bosnia & Herzegovina)',
    'sr-sp': 'Serbian (Serbia & Montenegro)',
    ss: 'Swati',
    st: 'Southern Sotho',
    su: 'Sundanese',
    sv: 'Swedish',
    'sv-fi': 'Swedish (Finland)',
    'sv-se': 'Swedish (Sweden)',
    sw: 'Swahili',
    sx: 'Sutu',
    syr: 'Syriac',
    ta: 'Tamil',
    te: 'Telugu',
    tg: 'Tajik',
    th: 'Thai',
    ti: 'Tigrinya',
    tk: 'Turkmen',
    tl: 'Filipino',
    tn: 'Tswana',
    to: 'Tongan',
    tr: 'Turkish',
    ts: 'Tsonga',
    tt: 'Tatar',
    tw: 'Akan',
    ty: 'Tahitian',
    ug: 'Uyghur',
    uk: 'Ukrainian',
    ur: 'Urdu',
    us: 'English (United States)',
    uz: 'Uzbek',
    ve: 'Venda',
    vi: 'Vietnamese',
    vo: 'Volapük',
    wa: 'Walloon',
    wo: 'Wolof',
    xh: 'Xhosa',
    yi: 'Yiddish',
    yo: 'Yoruba',
    za: 'Zhuang',
    zh: 'Chinese',
    'zh-cn': 'Chinese (China)',
    'zh-hk': 'Chinese (Hong Kong SAR China)',
    'zh-mo': 'Chinese (Macao SAR China)',
    'zh-sg': 'Chinese (Singapore)',
    'zh-tw': 'Chinese (Taiwan)',
    zu: 'Zulu'
};
